import type { Node, Edge, PVSiteData } from '../types';
import type { MappedEntity, MappedConnection } from '../../../hooks/useLazyDetailedGraph';

/**
 * Build a Map of entity ID → entity for O(1) lookups during edge mapping.
 * Keeps first occurrence (matching Array.find behavior).
 */
export const buildEntityLookup = (entities: MappedEntity[]): Map<string, MappedEntity> => {
  const entityById = new Map<string, MappedEntity>();
  entities.forEach(e => {
    const id = e.entity_id || (e as any).id;
    if (id && !entityById.has(id)) {
      entityById.set(id, e);
    }
  });
  return entityById;
};

/**
 * Map API entities to the Node format expected by processData/D3HybridViewer.
 */
export const mapEntitiesToNodes = (entities: MappedEntity[]): Node[] => {
  return entities.map(entity => {
    const entityName = entity.entity_name || (entity as any).name || (entity as any).primary_id;
    const componentType = entity.pvcollada_component_type || (entity as any).entity_type_name || (entity as any).concept_category || (entity as any).component_type || 'Unknown';
    const metadata = entity.entity_metadata || (entity as any).metadata || (entity as any).properties || {};

    // pref can be at top level of entity (from API) or in metadata
    const rawPref = entity.pref || metadata.pref || metadata.page_ref;
    let pref: any = undefined;
    if (rawPref) {
      if (typeof rawPref === 'object' && rawPref.p != null) {
        pref = rawPref.p;
      } else if (typeof rawPref === 'object' && rawPref.page != null) {
        pref = rawPref.page;
      } else {
        pref = rawPref;
      }
    }

    const entityAliases = entity.aliases;
    const aliases = Array.isArray(entityAliases) ? entityAliases : [];

    return {
      component_type: componentType.toLowerCase().replace(/ /g, '_'),
      "Primary ID": entityName,
      Aliases: aliases,
      component_order: entity.component_order || metadata.component_order || null,
      properties: metadata,
      configuration: entity.configuration || {},
      pref: pref,
      api_child_count: entity.api_child_count,
    };
  });
};

/**
 * Map API connections to the Edge format expected by processData/D3HybridViewer.
 * Uses entityById map for O(1) name resolution.
 */
export const mapConnectionsToEdges = (
  connections: MappedConnection[],
  entityById: Map<string, MappedEntity>
): { edges: Edge[]; missingSource: number; missingTarget: number } => {
  let missingSource = 0;
  let missingTarget = 0;

  const edges: Edge[] = connections.map(conn => {
    const sourceEntity = entityById.get(conn.source_entity_id);
    const targetEntity = entityById.get(conn.target_entity_id);

    if (!sourceEntity) missingSource++;
    if (!targetEntity) missingTarget++;

    const sourceId = sourceEntity ? (sourceEntity.entity_name || (sourceEntity as any).name) : conn.source_entity_id;
    const targetId = targetEntity ? (targetEntity.entity_name || (targetEntity as any).name) : conn.target_entity_id;

    let signal = (conn.properties as any)?.signal || 'DC';
    const typeLower = conn.connection_type.toLowerCase();

    if (!(conn.properties as any)?.signal) {
      if (typeLower.includes('ac')) signal = 'AC';
      else if (typeLower.includes('data') || typeLower.includes('modbus') || typeLower.includes('comm')) signal = 'COMM';
    }

    return {
      source: sourceId,
      target: targetId,
      relation: conn.connection_type,
      signal: signal,
      properties: (conn.properties || {}) as any,
      configuration: (conn.configuration || {}) as any
    };
  });

  return { edges, missingSource, missingTarget };
};

/**
 * Check if connections have a high mismatch ratio with entities (> 10%).
 * Indicates data from different sites or incomplete load.
 */
export const hasConnectionMismatch = (
  connections: MappedConnection[],
  entityIds: Set<string>
): boolean => {
  if (connections.length === 0) return false;
  const badConns = connections.filter(c =>
    !entityIds.has(c.source_entity_id) || !entityIds.has(c.target_entity_id)
  );
  return badConns.length / connections.length > 0.1;
};

/**
 * Verify entities belong to the currently selected site.
 */
export const isDataFromCorrectSite = (entities: MappedEntity[], currentSiteId: string | undefined): boolean => {
  if (!currentSiteId || entities.length === 0) return true;
  const firstEntitySiteId = entities[0]?.site_id;
  return !firstEntitySiteId || firstEntitySiteId === currentSiteId;
};

/**
 * Wrap nodes and edges in the PVSiteData format for processData().
 */
export const buildPVSiteData = (nodes: Node[], edges: Edge[]): PVSiteData => ({
  nodes,
  edges,
  project_metdata: {
    project_name: "Solar Farm",
    project_address: null,
    project_lat_lon: null,
    project_capacity_mw: null
  }
});
